import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { clientsAPI, firmAPI } from '../lib/api';
import { useAutoSave, AutoSaveIndicator } from '../hooks/useAutoSave';
import { formatDate, formatDateTime, formatCurrency, getStatusColor, formatRelativeTime, downloadFile, openFileInNewTab } from '../lib/utils';
import { 
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  ArrowLeft,
  Briefcase,
  FileText,
  CheckSquare,
  Calendar,
  DollarSign,
  Wallet,
  Clock,
  Users,
  Edit,
  Plus,
  ExternalLink,
  MessageSquare,
  Activity,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Circle,
  ChevronRight,
  Send,
  Eye,
  FileDown,
  Scale,
  Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const ClientProfile = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [inviteDialog, setInviteDialog] = useState(false);
  const [portalViewDialog, setPortalViewDialog] = useState(false);
  const [portalData, setPortalData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Edit Client state
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    notes: ''
  });
  
  // New Case state
  const [newCaseDialog, setNewCaseDialog] = useState(false);
  const [newCaseForm, setNewCaseForm] = useState({
    title: '',
    practice_area: '',
    description: '',
    case_number: '',
    court_name: '',
    status: 'open',
    opposing_party: '',
    opposing_counsel: ''
  });
  const [practiceAreas, setPracticeAreas] = useState([]);
  
  // Court Summary state
  const [courtSummaryDialog, setCourtSummaryDialog] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [firmSettings, setFirmSettings] = useState(null);
  const [courtSummaryForm, setCourtSummaryForm] = useState({
    case_id: '',
    jurisdiction: 'Ontario',
    court_name: 'Superior Court of Justice',
    include_billing: false,
    include_communications: true,
    output_type: 'court',
    redact_pii: false,
    redact_minors: true
  });

  // Auto-save hook for edit client form
  const saveClient = useCallback(async (formData) => {
    if (!clientId || !formData.first_name || !formData.last_name) return;
    await clientsAPI.update(clientId, formData);
    // Refresh client data after auto-save
    const profileRes = await clientsAPI.getProfile(clientId);
    setProfile(profileRes.data);
  }, [clientId]);

  const { saveStatus: clientSaveStatus, lastSaved: clientLastSaved, hasChanges: clientHasChanges, saveNow: saveClientNow, reset: resetClientSave } = useAutoSave(
    editForm,
    saveClient,
    {
      delay: 1500,
      enabled: editDialog && editForm.first_name && editForm.last_name,
      showToast: false,
      onSaveError: (error) => {
        console.error('Auto-save failed:', error);
      }
    }
  );

  useEffect(() => {
    fetchClientProfile();
    fetchFirmSettings();
    fetchPracticeAreas();
  }, [clientId]);

  const fetchPracticeAreas = async () => {
    try {
      // Use the enabled practice areas endpoint which already filters by firm settings
      const areasRes = await firmAPI.getEnabledPracticeAreas();
      const enabledAreas = areasRes.data || [];
      setPracticeAreas(enabledAreas);
    } catch (error) {
      console.error('Failed to fetch practice areas:', error);
      // Set empty array if both fail
      setPracticeAreas([]);
    }
  };

  const fetchClientProfile = async () => {
    try {
      const [profileRes, timelineRes] = await Promise.all([
        clientsAPI.getProfile(clientId),
        clientsAPI.getTimeline(clientId)
      ]);
      setProfile(profileRes.data);
      setTimeline(timelineRes.data.timeline || []);
    } catch (error) {
      console.error('Failed to fetch client profile:', error);
      if (error.response?.status === 404) {
        navigate('/clients');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFirmSettings = async () => {
    try {
      const response = await firmAPI.getSettings();
      setFirmSettings(response.data);
      // Apply firm defaults
      setCourtSummaryForm(prev => ({
        ...prev,
        jurisdiction: response.data.default_jurisdiction || 'Ontario',
        court_name: response.data.default_court || 'Superior Court of Justice',
        include_billing: response.data.billing_in_reports || false
      }));
    } catch (error) {
      console.error('Failed to fetch firm settings:', error);
    }
  };

  const handleGenerateCourtSummary = async (openInTab = false) => {
    if (!courtSummaryForm.case_id) {
      toast.error('Please select a case');
      return;
    }
    
    setGeneratingPdf(true);
    try {
      const response = await clientsAPI.generateCourtSummary(clientId, courtSummaryForm);
      
      // Extract filename from response headers or generate one
      const contentDisposition = response.headers?.['content-disposition'];
      let filename = `Court_Summary_${clientId}_${new Date().toISOString().split('T')[0]}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }
      
      if (openInTab) {
        // Open in new tab
        const success = openFileInNewTab(response.data, 'application/pdf');
        if (success) {
          toast.success('PDF opened in new tab');
          setCourtSummaryDialog(false);
        } else {
          toast.error('Failed to open PDF');
        }
      } else {
        // Try to download
        const success = downloadFile(response.data, filename, 'application/pdf');
        if (success) {
          toast.success('Court-Ready Summary downloaded successfully');
          setCourtSummaryDialog(false);
        } else {
          toast.error('Download failed - try "Open in New Tab" instead');
        }
      }
    } catch (error) {
      console.error('Failed to generate court summary:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to generate court summary');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleInviteToPortal = async (e) => {
    e.preventDefault();
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setSubmitting(true);
    try {
      const response = await clientsAPI.inviteToPortal(clientId, {
        client_id: clientId,
        email: inviteEmail,
        message: inviteMessage
      });
      
      // Check if email was actually sent
      if (response.data.email_sent) {
        toast.success(`Invitation sent successfully via ${response.data.email_provider === 'gmail' ? 'Gmail' : 'email'}`);
      } else {
        // Invitation created but email not sent
        toast.warning(response.data.error || 'Invitation created but email could not be sent. Please connect Gmail in Communication settings.');
      }
      
      setInviteDialog(false);
      setInviteEmail('');
      setInviteMessage('');
      fetchClientProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewPortal = async () => {
    try {
      const response = await clientsAPI.getPortalView(clientId);
      setPortalData(response.data);
      setPortalViewDialog(true);
    } catch (err) {
      toast.error('Failed to load portal view');
    }
  };

  const handleOpenEditDialog = () => {
    if (client) {
      setEditForm({
        first_name: client.first_name || client.name?.split(' ')[0] || '',
        last_name: client.last_name || client.name?.split(' ').slice(1).join(' ') || '',
        email: client.email || '',
        phone: client.phone || '',
        company: client.company || '',
        address: client.address || '',
        notes: client.notes || ''
      });
      setEditDialog(true);
    }
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await clientsAPI.update(clientId, editForm);
      toast.success('Client updated successfully');
      setEditDialog(false);
      fetchClientProfile();
    } catch (error) {
      console.error('Failed to update client:', error);
      toast.error(error.response?.data?.detail || 'Failed to update client');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenNewCaseDialog = () => {
    setNewCaseForm({
      title: '',
      practice_area: practiceAreas[0]?.name || '',
      description: '',
      case_number: '',
      court_name: '',
      status: 'open',
      opposing_party: '',
      opposing_counsel: ''
    });
    setNewCaseDialog(true);
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    if (!newCaseForm.title || !newCaseForm.practice_area) {
      toast.error('Please fill in required fields');
      return;
    }
    setSubmitting(true);
    try {
      const caseData = {
        title: newCaseForm.title,
        practice_area: newCaseForm.practice_area,
        description: newCaseForm.description,
        case_number: newCaseForm.case_number,
        court_name: newCaseForm.court_name,
        status: newCaseForm.status,
        opposing_party: newCaseForm.opposing_party,
        opposing_counsel: newCaseForm.opposing_counsel,
        client_id: clientId
      };
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(caseData)
      });
      if (!response.ok) throw new Error('Failed to create case');
      toast.success('Case created successfully');
      setNewCaseDialog(false);
      fetchClientProfile();
    } catch (error) {
      console.error('Failed to create case:', error);
      toast.error(error.message || 'Failed to create case');
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getTimelineIcon = (type) => {
    const icons = {
      client_created: { icon: User, color: 'text-blue-600', bg: 'bg-blue-100' },
      case_created: { icon: Briefcase, color: 'text-green-600', bg: 'bg-green-100' },
      case_updated: { icon: Briefcase, color: 'text-yellow-600', bg: 'bg-yellow-100' },
      document_added: { icon: FileText, color: 'text-purple-600', bg: 'bg-purple-100' },
      task_created: { icon: CheckSquare, color: 'text-orange-600', bg: 'bg-orange-100' },
      task_completed: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' },
      invoice_created: { icon: DollarSign, color: 'text-blue-600', bg: 'bg-blue-100' },
      payment_received: { icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-100' },
      trust_deposit: { icon: Wallet, color: 'text-green-600', bg: 'bg-green-100' },
      trust_withdrawal: { icon: Wallet, color: 'text-red-600', bg: 'bg-red-100' },
      court_date: { icon: Calendar, color: 'text-red-600', bg: 'bg-red-100' },
      note: { icon: MessageSquare, color: 'text-slate-600', bg: 'bg-slate-100' },
      activity: { icon: Activity, color: 'text-slate-600', bg: 'bg-slate-100' },
    };
    return icons[type] || { icon: Circle, color: 'text-slate-400', bg: 'bg-slate-100' };
  };

  if (loading) {
    return <PageLoader text="Loading client profile..." />;
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Client not found</p>
        <Button variant="link" onClick={() => navigate('/clients')}>
          Back to Clients
        </Button>
      </div>
    );
  }

  const { client, cases, documents, tasks, invoices, trust_transactions, important_dates, team_members, summary } = profile;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-profile-page">
      {/* Back Button & Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
      </div>

      {/* Client Header Card */}
      <Card data-testid="client-header-card">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="flex items-start gap-4">
              <Avatar className="h-20 w-20">
                <AvatarFallback className="bg-blue-600 text-white text-2xl">
                  {getInitials(client.name)}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                    {client.name}
                  </h1>
                  <Badge className={getStatusColor(client.status)}>
                    {client.status}
                  </Badge>
                </div>
                {client.company && (
                  <p className="text-slate-600 flex items-center gap-2 mt-1">
                    <Building className="h-4 w-4" />
                    {client.company}
                  </p>
                )}
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-500">
                  {client.email && (
                    <a href={`mailto:${client.email}`} className="flex items-center gap-1 hover:text-blue-600">
                      <Mail className="h-4 w-4" />
                      {client.email}
                    </a>
                  )}
                  {client.phone && (
                    <a href={`tel:${client.phone}`} className="flex items-center gap-1 hover:text-blue-600">
                      <Phone className="h-4 w-4" />
                      {client.phone}
                    </a>
                  )}
                  {client.address && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {client.address}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Court-Ready Summary Button - Primary Action */}
              {cases.length > 0 && (
                <Button 
                  size="sm" 
                  className="gap-2 bg-amber-600 hover:bg-amber-700" 
                  onClick={() => {
                    setCourtSummaryForm(prev => ({ ...prev, case_id: cases[0]?.case_id || '' }));
                    setCourtSummaryDialog(true);
                  }}
                  data-testid="court-summary-btn"
                >
                  <Scale className="h-4 w-4" />
                  Court-Ready Summary
                </Button>
              )}
              
              {client.portal_status === 'active' ? (
                <Button variant="outline" size="sm" className="gap-2" onClick={handleViewPortal} data-testid="view-portal-btn">
                  <Eye className="h-4 w-4" />
                  View Portal
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="gap-2" onClick={() => { setInviteEmail(client.email || ''); setInviteDialog(true); }} data-testid="invite-portal-btn">
                  <Send className="h-4 w-4" />
                  {client.portal_status === 'invited' ? 'Resend Invite' : 'Invite to Portal'}
                </Button>
              )}
              <Button variant="outline" size="sm" className="gap-2" onClick={handleOpenEditDialog} data-testid="edit-client-btn">
                <Edit className="h-4 w-4" />
                Edit
              </Button>
              <Button size="sm" className="gap-2" onClick={handleOpenNewCaseDialog} data-testid="new-case-btn">
                <Plus className="h-4 w-4" />
                New Case
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="summary-stats">
        <Card>
          <CardContent className="p-4 text-center">
            <Briefcase className="h-6 w-6 mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{summary.total_cases}</p>
            <p className="text-xs text-slate-500">Total Cases</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Activity className="h-6 w-6 mx-auto text-green-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{summary.active_cases}</p>
            <p className="text-xs text-slate-500">Active Cases</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <FileText className="h-6 w-6 mx-auto text-purple-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{summary.total_documents}</p>
            <p className="text-xs text-slate-500">Documents</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckSquare className="h-6 w-6 mx-auto text-orange-600 mb-2" />
            <p className="text-2xl font-bold text-slate-900">{summary.pending_tasks}</p>
            <p className="text-xs text-slate-500">Pending Tasks</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <DollarSign className="h-6 w-6 mx-auto text-green-600 mb-2" />
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.total_paid)}</p>
            <p className="text-xs text-slate-500">Total Paid</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Wallet className="h-6 w-6 mx-auto text-blue-600 mb-2" />
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(summary.trust_balance)}</p>
            <p className="text-xs text-slate-500">Trust Balance</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-5 max-w-2xl">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="cases" data-testid="tab-cases">Cases ({cases.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">Billing</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Important Dates */}
            <Card className="lg:col-span-2" data-testid="important-dates-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-red-500" />
                  Important Dates
                </CardTitle>
              </CardHeader>
              <CardContent>
                {important_dates.length > 0 ? (
                  <div className="space-y-3">
                    {important_dates.slice(0, 10).map((date, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            date.type === 'court_date' ? 'bg-red-100' : 
                            date.type === 'invoice_due' ? 'bg-orange-100' : 'bg-blue-100'
                          }`}>
                            {date.type === 'court_date' ? (
                              <Calendar className="h-5 w-5 text-red-600" />
                            ) : date.type === 'invoice_due' ? (
                              <DollarSign className="h-5 w-5 text-orange-600" />
                            ) : (
                              <Clock className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{date.title}</p>
                            {date.case_title && (
                              <p className="text-sm text-slate-500">{date.case_title}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-slate-900">{formatDate(date.date)}</p>
                          <Badge variant="outline" className="text-xs capitalize">
                            {date.type.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <p>No upcoming dates</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Team Members */}
            <Card data-testid="team-members-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-500" />
                  Team Members
                </CardTitle>
                <CardDescription>People working on this client</CardDescription>
              </CardHeader>
              <CardContent>
                {team_members.length > 0 ? (
                  <div className="space-y-3">
                    {team_members.map((member) => (
                      <div key={member.user_id} className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={member.picture} />
                          <AvatarFallback className="bg-slate-200 text-slate-700 text-sm">
                            {getInitials(member.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-slate-900">{member.name}</p>
                          <p className="text-xs text-slate-500">{member.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <Users className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm">No team assigned yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            {client.notes && (
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle className="text-lg">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 whitespace-pre-wrap">{client.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Cases Tab */}
        <TabsContent value="cases">
          <Card data-testid="cases-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Cases</CardTitle>
              <Button size="sm" className="gap-2" onClick={handleOpenNewCaseDialog}>
                <Plus className="h-4 w-4" />
                New Case
              </Button>
            </CardHeader>
            <CardContent>
              {cases.length > 0 ? (
                <div className="space-y-4">
                  {cases.map((caseItem) => (
                    <div 
                      key={caseItem.case_id}
                      className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer"
                      onClick={() => navigate(`/cases/${caseItem.case_id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-slate-900">{caseItem.title}</h4>
                            <Badge className={getStatusColor(caseItem.status)}>
                              {caseItem.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{caseItem.case_number}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge variant="outline">{caseItem.practice_area}</Badge>
                            {caseItem.court_name && (
                              <Badge variant="secondary">{caseItem.court_name}</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Opened</p>
                          <p className="text-sm font-medium">{formatDate(caseItem.created_at)}</p>
                          {caseItem.court_dates?.length > 0 && (
                            <p className="text-xs text-red-600 mt-1">
                              {caseItem.court_dates.length} court date(s)
                            </p>
                          )}
                        </div>
                      </div>
                      {caseItem.description && (
                        <p className="text-sm text-slate-600 mt-3 line-clamp-2">{caseItem.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Briefcase className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p>No cases for this client</p>
                  <Button variant="link" onClick={handleOpenNewCaseDialog}>
                    Create a new case
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card data-testid="documents-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Documents</CardTitle>
              <Button size="sm" className="gap-2" onClick={() => navigate('/documents')}>
                <Plus className="h-4 w-4" />
                Add Document
              </Button>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Document</th>
                        <th>Type</th>
                        <th>Case</th>
                        <th>Date Added</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc) => (
                        <tr key={doc.document_id}>
                          <td>
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-slate-400" />
                              <span className="font-medium">{doc.name}</span>
                            </div>
                          </td>
                          <td>
                            <Badge variant="outline" className="capitalize">
                              {doc.type?.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="text-slate-500">
                            {cases.find(c => c.case_id === doc.case_id)?.title || '-'}
                          </td>
                          <td className="text-slate-500">{formatDate(doc.created_at)}</td>
                          <td>
                            {doc.is_signed ? (
                              <Badge className="bg-green-100 text-green-800">Signed</Badge>
                            ) : (
                              <Badge variant="secondary">Draft</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p>No documents for this client</p>
                  <Button variant="link" onClick={() => navigate('/documents')}>
                    Add a document
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Invoices */}
            <Card data-testid="invoices-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Invoices
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoices.length > 0 ? (
                  <div className="space-y-3">
                    {invoices.map((invoice) => (
                      <div key={invoice.invoice_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                        <div>
                          <p className="font-medium">{invoice.invoice_number}</p>
                          <p className="text-sm text-slate-500">Due: {formatDate(invoice.due_date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(invoice.amount)}</p>
                          <Badge className={getStatusColor(invoice.status)}>
                            {invoice.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <DollarSign className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm">No invoices yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Trust Account */}
            <Card data-testid="trust-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="h-5 w-5" />
                  Trust Account
                </CardTitle>
                <CardDescription>
                  Current Balance: <span className="font-bold text-green-600">{formatCurrency(summary.trust_balance)}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                {trust_transactions.length > 0 ? (
                  <div className="space-y-3">
                    {trust_transactions.slice(0, 5).map((trans) => (
                      <div key={trans.transaction_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                        <div>
                          <p className="font-medium capitalize">{trans.type}</p>
                          <p className="text-sm text-slate-500">{trans.description}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${trans.type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                            {trans.type === 'deposit' ? '+' : '-'}{formatCurrency(trans.amount)}
                          </p>
                          <p className="text-xs text-slate-500">{formatDate(trans.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Wallet className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm">No trust transactions</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <Card data-testid="timeline-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Activity Timeline
              </CardTitle>
              <CardDescription>Complete history of all changes and activities</CardDescription>
            </CardHeader>
            <CardContent>
              {timeline.length > 0 ? (
                <ScrollArea className="h-[600px] pr-4">
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200" />
                    
                    <div className="space-y-6">
                      {timeline.map((item, idx) => {
                        const { icon: Icon, color, bg } = getTimelineIcon(item.type);
                        return (
                          <div key={idx} className="relative flex gap-4 pl-2">
                            <div className={`relative z-10 h-10 w-10 rounded-full ${bg} flex items-center justify-center`}>
                              <Icon className={`h-5 w-5 ${color}`} />
                            </div>
                            <div className="flex-1 pt-1">
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-slate-900">{item.title}</p>
                                  <p className="text-sm text-slate-500">{item.description}</p>
                                </div>
                                <div className="text-right flex-shrink-0 ml-4">
                                  <p className="text-sm text-slate-500">{formatDate(item.date)}</p>
                                  <p className="text-xs text-slate-400">{formatRelativeTime(item.date)}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Activity className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p>No activity recorded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite to Portal Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Client to Portal</DialogTitle>
            <DialogDescription>
              Send an invitation to {client.name} to access their client portal
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteToPortal} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="inviteEmail">Email Address</Label>
              <Input
                id="inviteEmail"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="client@email.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inviteMessage">Personal Message (Optional)</Label>
              <Textarea
                id="inviteMessage"
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                placeholder="Add a personal message to the invitation..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setInviteDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Portal Dialog */}
      <Dialog open={portalViewDialog} onOpenChange={setPortalViewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Client Portal View</DialogTitle>
            <DialogDescription>
              This is what {client.name} sees in their portal
            </DialogDescription>
          </DialogHeader>
          {portalData && (
            <div className="space-y-6 mt-4">
              {/* Portal Status */}
              <div className="flex items-center gap-2">
                <Badge className={portalData.portal_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                  Portal: {portalData.portal_status}
                </Badge>
                {portalData.portal_email && (
                  <span className="text-sm text-slate-500">({portalData.portal_email})</span>
                )}
              </div>

              {/* Stats Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-slate-50 text-center">
                  <p className="text-2xl font-bold">{portalData.stats.active_cases}</p>
                  <p className="text-xs text-slate-500">Active Cases</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 text-center">
                  <p className="text-2xl font-bold">{portalData.stats.total_documents}</p>
                  <p className="text-xs text-slate-500">Documents</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 text-center">
                  <p className="text-2xl font-bold">{portalData.stats.pending_invoices}</p>
                  <p className="text-xs text-slate-500">Pending Invoices</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 text-center">
                  <p className="text-2xl font-bold text-orange-600">{formatCurrency(portalData.stats.total_owed)}</p>
                  <p className="text-xs text-slate-500">Amount Due</p>
                </div>
              </div>

              {/* Cases */}
              <div>
                <h4 className="font-medium mb-2">Cases ({portalData.cases.length})</h4>
                {portalData.cases.length > 0 ? (
                  <div className="space-y-2">
                    {portalData.cases.slice(0, 3).map((c) => (
                      <div key={c.case_id} className="p-3 rounded-lg border border-slate-200 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{c.title}</p>
                          <p className="text-sm text-slate-500">{c.case_number}</p>
                        </div>
                        <Badge className={getStatusColor(c.status)}>{c.status}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No cases</p>
                )}
              </div>

              {/* Upcoming Dates */}
              {portalData.upcoming_dates?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Upcoming Dates</h4>
                  <div className="space-y-2">
                    {portalData.upcoming_dates.map((d, i) => (
                      <div key={i} className="p-3 rounded-lg bg-red-50 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{d.title}</p>
                          <p className="text-sm text-slate-500">{d.case_title}</p>
                        </div>
                        <Badge variant="outline">{formatDate(d.date)}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Court-Ready Summary Dialog */}
      <Dialog open={courtSummaryDialog} onOpenChange={setCourtSummaryDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-amber-600" />
              Generate Court-Ready Summary
            </DialogTitle>
            <DialogDescription>
              Generate a neutral, defensible PDF suitable for Canadian legal proceedings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Case Selection */}
            <div className="space-y-2">
              <Label htmlFor="caseSelect">Select Case *</Label>
              <Select 
                value={courtSummaryForm.case_id} 
                onValueChange={(v) => setCourtSummaryForm(prev => ({ ...prev, case_id: v }))}
              >
                <SelectTrigger data-testid="case-select">
                  <SelectValue placeholder="Select a case" />
                </SelectTrigger>
                <SelectContent>
                  {cases.map(c => (
                    <SelectItem key={c.case_id} value={c.case_id}>
                      {c.title} ({c.case_number || 'Pending'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Jurisdiction */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="jurisdiction">Jurisdiction</Label>
                <Select 
                  value={courtSummaryForm.jurisdiction} 
                  onValueChange={(v) => setCourtSummaryForm(prev => ({ ...prev, jurisdiction: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ontario">Ontario</SelectItem>
                    <SelectItem value="British Columbia">British Columbia</SelectItem>
                    <SelectItem value="Alberta">Alberta</SelectItem>
                    <SelectItem value="Quebec">Quebec</SelectItem>
                    <SelectItem value="Manitoba">Manitoba</SelectItem>
                    <SelectItem value="Saskatchewan">Saskatchewan</SelectItem>
                    <SelectItem value="Nova Scotia">Nova Scotia</SelectItem>
                    <SelectItem value="New Brunswick">New Brunswick</SelectItem>
                    <SelectItem value="Federal">Federal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="court">Court</Label>
                <Select 
                  value={courtSummaryForm.court_name} 
                  onValueChange={(v) => setCourtSummaryForm(prev => ({ ...prev, court_name: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Superior Court of Justice">Superior Court of Justice</SelectItem>
                    <SelectItem value="Court of Appeal">Court of Appeal</SelectItem>
                    <SelectItem value="Small Claims Court">Small Claims Court</SelectItem>
                    <SelectItem value="Ontario Court of Justice">Ontario Court of Justice</SelectItem>
                    <SelectItem value="Federal Court">Federal Court</SelectItem>
                    <SelectItem value="Federal Court of Appeal">Federal Court of Appeal</SelectItem>
                    <SelectItem value="Supreme Court of Canada">Supreme Court of Canada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Output Type */}
            <div className="space-y-2">
              <Label>Output Type</Label>
              <Select 
                value={courtSummaryForm.output_type} 
                onValueChange={(v) => setCourtSummaryForm(prev => ({ ...prev, output_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="court">Court Submission</SelectItem>
                  <SelectItem value="opposing_counsel">Opposing Legal Representative</SelectItem>
                  <SelectItem value="internal">Internal Use</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Options */}
            <div className="space-y-4 pt-2 border-t border-slate-100">
              <p className="text-sm font-medium text-slate-700">Include Sections</p>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="includeBilling" className="text-sm">Billing & Time Summary</Label>
                  <p className="text-xs text-slate-500">Include hours and invoice summary</p>
                </div>
                <Switch
                  id="includeBilling"
                  checked={courtSummaryForm.include_billing}
                  onCheckedChange={(v) => setCourtSummaryForm(prev => ({ ...prev, include_billing: v }))}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="includeComms" className="text-sm">Communications Log</Label>
                  <p className="text-xs text-slate-500">External communications only</p>
                </div>
                <Switch
                  id="includeComms"
                  checked={courtSummaryForm.include_communications}
                  onCheckedChange={(v) => setCourtSummaryForm(prev => ({ ...prev, include_communications: v }))}
                />
              </div>
            </div>

            {/* Redaction Options */}
            <div className="space-y-4 pt-2 border-t border-slate-100">
              <p className="text-sm font-medium text-slate-700">Privacy & Redaction</p>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="redactMinors" className="text-sm">Protect Minor Information</Label>
                  <p className="text-xs text-slate-500">Redact names of minors</p>
                </div>
                <Switch
                  id="redactMinors"
                  checked={courtSummaryForm.redact_minors}
                  onCheckedChange={(v) => setCourtSummaryForm(prev => ({ ...prev, redact_minors: v }))}
                />
              </div>
            </div>

            {/* Disclaimer */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> This report is a factual summary and does not constitute legal argument or opinion. 
                All information is sourced from firm records with timestamps and audit trails.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setCourtSummaryDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="outline"
                onClick={() => handleGenerateCourtSummary(true)} 
                disabled={generatingPdf || !courtSummaryForm.case_id}
                className="gap-2"
                data-testid="open-in-tab-btn"
              >
                <ExternalLink className="h-4 w-4" />
                Open in New Tab
              </Button>
              <Button 
                onClick={() => handleGenerateCourtSummary(false)} 
                disabled={generatingPdf || !courtSummaryForm.case_id}
                className="gap-2 bg-amber-600 hover:bg-amber-700"
                data-testid="download-pdf-btn"
              >
                {generatingPdf ? (
                  <>Generating...</>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Download PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={editDialog} onOpenChange={(open) => {
        if (!open && clientHasChanges) {
          saveClientNow();
        }
        setEditDialog(open);
        if (!open) {
          resetClientSave();
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Edit Client</DialogTitle>
                <DialogDescription>Update client information - changes auto-save</DialogDescription>
              </div>
              <AutoSaveIndicator status={clientSaveStatus} lastSaved={clientLastSaved} className="mr-4" />
            </div>
          </DialogHeader>
          <form onSubmit={handleSaveClient} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  required
                  data-testid="edit-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  required
                  data-testid="edit-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editEmail">Email</Label>
              <Input
                id="editEmail"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="edit-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editPhone">Phone</Label>
              <Input
                id="editPhone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                data-testid="edit-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editCompany">Company</Label>
              <Input
                id="editCompany"
                value={editForm.company}
                onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                data-testid="edit-company"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editAddress">Address</Label>
              <Input
                id="editAddress"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                data-testid="edit-address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editNotes">Notes</Label>
              <Textarea
                id="editNotes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                rows={3}
                data-testid="edit-notes"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditDialog(false)}>
                Close
              </Button>
              <Button 
                type="button" 
                onClick={async () => {
                  await saveClientNow();
                  setEditDialog(false);
                  fetchClientProfile();
                }}
                disabled={clientSaveStatus === 'saving'}
                data-testid="save-client-btn"
              >
                {clientSaveStatus === 'saving' ? 'Saving...' : 'Save & Close'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Case Dialog */}
      <Dialog open={newCaseDialog} onOpenChange={setNewCaseDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Case</DialogTitle>
            <DialogDescription>
              Create a new case for {client?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCase} className="space-y-4 mt-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 mb-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Client: {client?.name}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="caseTitle">Case Title *</Label>
                <Input
                  id="caseTitle"
                  value={newCaseForm.title}
                  onChange={(e) => setNewCaseForm({ ...newCaseForm, title: e.target.value })}
                  placeholder="Enter case title"
                  required
                  data-testid="new-case-title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="casePracticeArea">Practice Area *</Label>
                <Select 
                  value={newCaseForm.practice_area} 
                  onValueChange={(v) => setNewCaseForm({ ...newCaseForm, practice_area: v })}
                >
                  <SelectTrigger data-testid="new-case-practice-area">
                    <SelectValue placeholder="Select practice area" />
                  </SelectTrigger>
                  <SelectContent>
                    {practiceAreas.map(area => (
                      <SelectItem key={area.id} value={area.name}>
                        {area.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="caseNumber">Case/Citation Number</Label>
                <Input
                  id="caseNumber"
                  value={newCaseForm.case_number}
                  onChange={(e) => setNewCaseForm({ ...newCaseForm, case_number: e.target.value })}
                  placeholder="e.g., 2024 SCC 30"
                  data-testid="new-case-number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="courtName">Court</Label>
                <Input
                  id="courtName"
                  value={newCaseForm.court_name}
                  onChange={(e) => setNewCaseForm({ ...newCaseForm, court_name: e.target.value })}
                  placeholder="e.g., Superior Court of Justice"
                  data-testid="new-case-court"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="opposingParty">Opposing Party</Label>
                <Input
                  id="opposingParty"
                  value={newCaseForm.opposing_party}
                  onChange={(e) => setNewCaseForm({ ...newCaseForm, opposing_party: e.target.value })}
                  placeholder="Enter opposing party name"
                  data-testid="new-case-opposing-party"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="opposingCounsel">Opposing Legal Representative</Label>
                <Input
                  id="opposingCounsel"
                  value={newCaseForm.opposing_counsel}
                  onChange={(e) => setNewCaseForm({ ...newCaseForm, opposing_counsel: e.target.value })}
                  placeholder="Enter opposing legal representative"
                  data-testid="new-case-opposing-counsel"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="caseDescription">Description</Label>
                <Textarea
                  id="caseDescription"
                  value={newCaseForm.description}
                  onChange={(e) => setNewCaseForm({ ...newCaseForm, description: e.target.value })}
                  placeholder="Brief description of the case..."
                  rows={3}
                  data-testid="new-case-description"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setNewCaseDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="create-case-btn">
                {submitting ? 'Creating...' : 'Create Case'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientProfile;
